<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Information List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Information
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="platforms"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-info me-2" @click="viewPlatform(item as Platform)">
                            <i class="fa fa-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-warning me-2" @click="editPlatform(item as Platform)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deletePlatform(item as Platform)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="platformModal" tabindex="-1" ref="modalRef" aria-labelledby="platformModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="savePlatform">Save</button>
                    </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="viewPlatformModal" tabindex="-1" ref="viewModalRef">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Platform</h5>
                    <button type="button" class="btn-close" @click="closeViewModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedPlatform">
                        <div class="mb-2"><strong>Code:</strong> {{ selectedPlatform.code }}</div>
                        <div class="mb-2"><strong>Name:</strong> {{ selectedPlatform.name }}</div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const platforms = ref<Platform[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    code: '',
    name: ''
}

const form = reactive({
    ...initForm
})

const dataPlatform: Platform[] = []
import * as master from "@/api/services/MasterRequest";

const fetchPlatform = async () => {
    loading.value = true
    try {
        const resp = await master.postRequest("/platform/list", {...params});
        dataPlatform.splice(0, dataPlatform.length);
        dataPlatform.push(...resp.data);

        let filteredPlatform = [...dataPlatform]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredPlatform = dataPlatform.filter(platform =>
            platform.code.toLowerCase().includes(searchLower) ||
            platform.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        platforms.value = filteredPlatform.slice(start, end)
        total.value = filteredPlatform.length

    } catch (error) {
        toast.error('Failed to fetch platforms')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchPlatform()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedPlatform = ref<Bank | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editPlatform = (platform: Platform) => {
    openModal(platform);
}

const savePlatform = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await master.postRequest("/platform/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Platform updated successfully');
                    closeModal();
                    fetchPlatform();
                } else {
                    toast.error('Failed to update Platform');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update Platform');
            })
        } else {
            await master.postRequest("/platform/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Platform created successfully');
                    closeModal();
                    fetchPlatform();
                } else {
                    toast.error('Failed to create platform');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create platform');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save platform');
    } finally {
        loading.value = false
    }
}

const deletePlatform = async (platform: Platform) => {
    if (!confirm('Are you sure you want to delete this platform?')) return

    loading.value = true
    try {
        await master.postRequest("/platform/delete", {ID: platform.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Platform deleted successfully');
                closeModal();
                fetchPlatform();
            } else {
                toast.error('Failed to delete platform');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete platform');
    } finally {
        loading.value = false
    }
}


const openModal = (platform: Platform | null = null) => {
    if (platform) {
        modalTitle.value = 'Edit Information'
        Object.assign(form, platform);
    } else {
        modalTitle.value = 'Create Information'
        Object.assign(form, {...initForm})
    }
    if (bsModal) {
        bsModal.show()
    }
}

const viewPlatform = (platform: Platform) => {
    selectedPlatform.value = platform
    if (!bsViewModal && viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    if (bsViewModal) {
        bsViewModal.show()
    }
}

const closeViewModal = () => {
    if (bsViewModal) {
        bsViewModal.hide()
    }
    selectedPlatform.value = null
}
</script>